/* theme.js — TNE Gifts GLOBAL module (chrome, search, tabs, carousels, reveal).
   Owns: drawers/modals (a11y), toast, predictive search, gallery thumbs, char
   counters, tabbed panels, horizontal carousels, reveal-on-scroll (.rise).
   Cart engine → cart.js · PDP → product.js · wishlist → wishlist.js.
   Exposes window.TNE for those files. Progressive enhancement: content is visible
   without JS; JS *arms* animations and AJAX. */
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

/* ---------- Money (shared) ---------- */
function money(cents) {
  const fmt = (window.theme && window.theme.moneyFormat) || '{{amount}}';
  const amt = (cents / 100).toLocaleString(document.documentElement.lang === 'ar' ? 'ar-AE' : 'en-AE',
    { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amt);
}

/* ---------- Drawers / modals (focus-trapped, inert when closed) ---------- */
let lastFocused = null;
const PANELS = '.drawer, .search-panel';
const focusables = (c) => [...c.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])')].filter((e) => e.offsetParent !== null);

function initPanels() {
  // Portal every drawer/panel to <body> so it escapes the sticky header's stacking context
  // (position:sticky creates one). Trapped inside it, the mobile-nav drawer renders BELOW the
  // page overlay (390) and becomes greyed/unclickable. Openers/closers reference by id, so
  // moving the node is transparent to the rest of the panel logic.
  $$(PANELS).forEach((p) => {
    if (p.parentElement === document.body) return;
    if (p.id) $$('body > [id="' + p.id + '"]').forEach((old) => { if (old !== p) old.remove(); }); // drop stale portaled copy (editor reload)
    document.body.appendChild(p);
  });
  $$(PANELS).forEach((p) => { if (p.getAttribute('data-open') !== 'true') { p.inert = true; p.setAttribute('aria-hidden', 'true'); } });
}
function openPanel(el) {
  if (!el) return;
  lastFocused = document.activeElement;
  el.setAttribute('data-open', 'true');
  el.inert = false; el.removeAttribute('aria-hidden');
  $('[data-overlay]')?.setAttribute('data-open', 'true');
  document.body.classList.add('no-scroll');
  (focusables(el)[0] || el).focus?.();
  el.addEventListener('keydown', trap);
  document.addEventListener('keydown', esc);
}
function closePanel(el) { if (el) { el.removeAttribute('data-open'); el.inert = true; el.setAttribute('aria-hidden', 'true'); afterClose(); } }
function closeAll() {
  $$('[data-open="true"]').forEach((el) => {
    el.removeAttribute('data-open');
    if (el.matches(PANELS)) { el.inert = true; el.setAttribute('aria-hidden', 'true'); }
    $$(`[aria-controls="${el.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
  });
  afterClose();
}
function afterClose() {
  if (!$('[data-open="true"]')) { document.body.classList.remove('no-scroll'); $('[data-overlay]')?.removeAttribute('data-open'); }
  document.removeEventListener('keydown', esc);
  lastFocused?.focus?.();
}
function esc(e) { if (e.key === 'Escape') closeAll(); }
function trap(e) {
  if (e.key !== 'Tab') return;
  const f = focusables(e.currentTarget); if (!f.length) return;
  const [first, last] = [f[0], f[f.length - 1]];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}
/* Delegated openers/closers (survive re-renders) */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-open-target]');
  if (opener) { opener.setAttribute('aria-expanded', 'true'); openPanel($(opener.getAttribute('data-open-target'))); }
  if (e.target.closest('[data-close]') || e.target.matches('[data-overlay]')) closeAll();
});

/* ---------- Toast ---------- */
function toast(msg) {
  let w = $('.toast-wrap'); if (!w) { w = document.createElement('div'); w.className = 'toast-wrap'; document.body.appendChild(w); }
  const t = document.createElement('div'); t.className = 'toast'; t.setAttribute('role', 'status'); t.textContent = msg;
  w.appendChild(t); setTimeout(() => t.remove(), 3000);
}

/* ---------- Predictive search (Section Rendering not needed; suggest.json) ---------- */
function initSearch() {
  const input = $('[data-predictive-search]'); if (!input) return;
  let tmr, ctrl;
  input.addEventListener('input', () => {
    clearTimeout(tmr);
    const q = input.value.trim();
    const box = $('#predictive-results'); if (!box) return;
    if (q.length < 2) { box.textContent = ''; return; }
    tmr = setTimeout(async () => {
      try {
        ctrl?.abort(); ctrl = new AbortController();
        const res = await fetch(`${(window.theme?.routes?.predictive_search_url) || '/search/suggest'}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`, { signal: ctrl.signal });
        const data = await res.json();
        if (input.value.trim() !== q) return;
        box.textContent = '';
        (data.resources?.results?.products || []).forEach((p) => {
          const a = document.createElement('a'); a.href = p.url; a.className = 'cluster'; a.style.cssText = 'gap:.75rem;padding:.4rem 0';
          if (p.featured_image?.url) { const im = document.createElement('img'); im.src = p.featured_image.url; im.width = 44; im.height = 44; im.alt = ''; im.loading = 'lazy'; im.style.cssText = 'border-radius:8px;object-fit:cover'; a.appendChild(im); }
          const s = document.createElement('span'); s.textContent = p.title; a.appendChild(s);
          box.appendChild(a);
        });
      } catch (_) {}
    }, 200);
  });
}

/* ---------- Gallery thumbs (PDP; harmless elsewhere) ---------- */
function initGallery() {
  $$('[data-thumb]').forEach((btn) => btn.addEventListener('click', () => {
    const main = $('#pdp-main-image'); if (main) main.src = btn.dataset.thumb;
    $$('[data-thumb]').forEach((b) => b.removeAttribute('aria-current'));
    btn.setAttribute('aria-current', 'true');
  }));
}

/* ---------- Character counters ---------- */
function initCounters() {
  const label = (window.TNE_STR && window.TNE_STR.chars_left) || 'left';
  $$('[data-counter]').forEach((ctrl) => {
    if (ctrl.__c) return; ctrl.__c = 1;
    const max = parseInt(ctrl.dataset.max, 10);
    const out = $(`[data-counter-for="${ctrl.id}"]`);
    const upd = () => { if (out) out.textContent = `${max - ctrl.value.length} ${label}`; };
    ctrl.addEventListener('input', upd); upd();
  });
}

/* ---------- Tabs ([data-tabs] > .tab[data-tab=panelId]; [data-panel][id]) ---------- */
function initTabs() {
  $$('[data-tabs]').forEach((bar) => {
    if (bar.__t) return; bar.__t = 1;
    bar.addEventListener('click', (e) => {
      const tab = e.target.closest('.tab[data-tab]'); if (!tab) return;
      const scope = bar.closest('[data-tabs-scope]') || document;
      bar.querySelectorAll('.tab').forEach((t) => t.classList.toggle('on', t === tab));
      tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      scope.querySelectorAll('[data-panel]').forEach((p) => { p.hidden = p.id !== tab.dataset.tab; });
      const shownPanel = document.getElementById(tab.dataset.tab); if (shownPanel) loadPanel(shownPanel);
      const cta = scope.querySelector('[data-tab-cta]');
      if (cta && tab.dataset.colUrl) { cta.href = tab.dataset.colUrl; if (tab.dataset.colLabel) cta.textContent = tab.dataset.colLabel; }
      window.dispatchEvent(new Event('resize'));  // let the shown carousel recompute
    });
  });
}

/* ---------- Lazy-load a hidden tab's products on first open (§2b) ---------- */
function loadPanel(panel) {
  const track = panel.querySelector('[data-lazy-src]');
  if (!track || track.dataset.loaded) return;
  track.dataset.loaded = '1';
  fetch(track.getAttribute('data-lazy-src')).then((r) => r.text()).then((html) => {
    track.innerHTML = html;
    window.dispatchEvent(new Event('resize')); // let the carousel recompute arrows
  }).catch(() => { track.removeAttribute('data-loaded'); });
}

/* ---------- Horizontal carousels ([data-carousel] > .carousel__track + prev/next) ---------- */
function initCarousels() {
  $$('[data-carousel]').forEach((car) => {
    if (car.__car) return; car.__car = 1;
    const track = car.querySelector('.carousel__track'); if (!track) return;
    const prev = car.querySelector('[data-carousel-prev]'), next = car.querySelector('[data-carousel-next]');
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || 260) + 16;
    const update = () => {
      const overflow = track.scrollWidth > track.clientWidth + 4;
      car.classList.toggle('is-static', !overflow);
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });
}
window.__initCarousels = initCarousels;

/* ---------- Reveal-on-scroll (.rise) — visible by default; JS arms the hide ---------- */
function initReveal() {
  const els = $$('.rise'); if (!els.length) return;
  document.documentElement.classList.add('rise-ready');
  if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('in')); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } });
  }, { rootMargin: '0px 0px -8% 0px' });
  els.forEach((el) => io.observe(el));
  // safety net: reveal anything left hidden
  addEventListener('load', () => setTimeout(() => $$('.rise:not(.in)').forEach((el) => el.classList.add('in')), 1200));
}

/* ---------- Mobile nav accordion + collection-nav triggers (delegated) ---------- */
document.addEventListener('click', (e) => {
  const acc = e.target.closest('[data-mnav-toggle]');
  if (acc) {
    const open = acc.getAttribute('aria-expanded') === 'true';
    acc.setAttribute('aria-expanded', String(!open));
    const sub = acc.nextElementSibling; if (sub) sub.hidden = open;
    return;
  }
  const trig = e.target.closest('[data-colnav-trigger]');
  if (trig) {
    // Touch/no-hover devices: click toggles. Hover devices open via mouseenter/focus instead
    // (a click there fires AFTER mouseenter already opened it → would toggle it closed).
    if (!matchMedia('(hover:hover)').matches) { const nav = trig.closest('[data-colnav]'); openColnavPanel(nav, trig, trig.getAttribute('aria-expanded') !== 'true'); }
    return;
  }
  if (!e.target.closest('.colnav')) closeColnavPanels();
});

/* ---------- Collection-nav: sub-links open in a PANEL BELOW the row (avoids overflow-clip) ---------- */
function closeColnavPanels(nav) {
  (nav ? [nav] : $$('[data-colnav]')).forEach((n) => {
    n.querySelectorAll('[data-colnav-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    const panel = n.querySelector('[data-colnav-mpanel]'); if (panel) { panel.hidden = true; panel.textContent = ''; }
  });
}
function openColnavPanel(nav, trig, open) {
  if (!nav || !trig) return;
  closeColnavPanels(nav);
  if (!open) return;
  const src = nav.querySelector(`[data-colnav-source="${trig.getAttribute('data-colnav-trigger')}"]`);
  const panel = nav.querySelector('[data-colnav-mpanel]');
  if (src && panel) { panel.innerHTML = src.innerHTML; panel.hidden = false; trig.setAttribute('aria-expanded', 'true'); }
}
function initColnav() {
  $$('[data-colnav]').forEach((nav) => {
    if (nav.__cn) return; nav.__cn = 1;
    const scroller = nav.querySelector('[data-colnav-scroller]');
    const prev = nav.querySelector('[data-colnav-prev]'), next = nav.querySelector('[data-colnav-next]');
    if (scroller) {
      const update = () => {
        const of = scroller.scrollWidth > scroller.clientWidth + 4;
        if (prev) prev.hidden = !of || scroller.scrollLeft <= 2;
        if (next) next.hidden = !of || scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
      };
      const by = () => Math.max(160, scroller.clientWidth * 0.7);
      prev?.addEventListener('click', () => scroller.scrollBy({ left: -by(), behavior: 'smooth' }));
      next?.addEventListener('click', () => scroller.scrollBy({ left: by(), behavior: 'smooth' }));
      scroller.addEventListener('scroll', update, { passive: true });
      addEventListener('resize', update);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(update); // recompute after web-fonts
      update();
    }
    // desktop: hover OR keyboard-focus opens the panel; leaving the nav closes it
    if (matchMedia('(hover:hover)').matches) {
      nav.querySelectorAll('[data-colnav-trigger]').forEach((t) => {
        t.addEventListener('mouseenter', () => openColnavPanel(nav, t, true));
        t.addEventListener('focus', () => openColnavPanel(nav, t, true));
      });
      nav.addEventListener('mouseleave', () => closeColnavPanels(nav));
    }
  });
}

/* ---------- Scrollable tab bars with edge arrows ([data-tabscroll] > .tab-bar) ---------- */
function initTabScroll() {
  $$('[data-tabscroll]').forEach((ts) => {
    if (ts.__ts) return; ts.__ts = 1;
    const bar = ts.querySelector('.tab-bar'); if (!bar) return;
    const prev = ts.querySelector('[data-tabscroll-prev]'), next = ts.querySelector('[data-tabscroll-next]');
    const update = () => {
      const of = bar.scrollWidth > bar.clientWidth + 4;
      if (prev) prev.hidden = !of || bar.scrollLeft <= 2;
      if (next) next.hidden = !of || bar.scrollLeft + bar.clientWidth >= bar.scrollWidth - 2;
    };
    const by = () => Math.max(120, bar.clientWidth * 0.6);
    prev?.addEventListener('click', () => bar.scrollBy({ left: -by(), behavior: 'smooth' }));
    next?.addEventListener('click', () => bar.scrollBy({ left: by(), behavior: 'smooth' }));
    bar.addEventListener('scroll', update, { passive: true });
    addEventListener('resize', update);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    update();
  });
}

/* ---------- Sticky header shrink (floating header) ---------- */
function initStickyHeader() {
  const header = $('.header'); if (!header || header.__sh) return; header.__sh = 1;
  let ticking = false, lastH = 0;
  // Publish the header's ACTUAL rendered height so sticky sub-bars (.col-sticky) pin flush
  // against it. --header-h is a fixed guess (68px); the header shrinks to ~57px when stuck,
  // and the mismatch left a visible gap under the floating filter bar.
  const setH = () => {
    const h = Math.round(header.getBoundingClientRect().height);
    if (h && h !== lastH) { lastH = h; document.documentElement.style.setProperty('--header-h-live', h + 'px'); }
  };
  const update = () => { header.classList.toggle('is-stuck', window.scrollY > 24); setH(); ticking = false; };
  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  addEventListener('resize', setH);
  header.addEventListener('transitionend', setH); // height settles after the shrink transition
  update();
}

/* ---------- Facet filter dropdowns: one open at a time + close on outside click / Escape ---------- */
function initFacets() {
  const facets = $$('.facet'); if (!facets.length) return;
  facets.forEach((d) => {
    if (d.__f) return; d.__f = 1;
    d.addEventListener('toggle', () => { if (d.open) facets.forEach((o) => { if (o !== d) o.open = false; }); });
  });
}
document.addEventListener('click', (e) => {
  if (!e.target.closest('.facet')) $$('.facet[open]').forEach((d) => { d.open = false; });
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') $$('.facet[open]').forEach((d) => { d.open = false; });
});

/* ---------- Shared API for cart.js / product.js / wishlist.js ---------- */
window.TNE = { $, $$, money, openPanel, closePanel, closeAll, toast };

/* ---------- Boot ---------- */
function boot() { initPanels(); initStickyHeader(); initSearch(); initGallery(); initCounters(); initTabs(); initTabScroll(); initCarousels(); initColnav(); initFacets(); initReveal(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', () => { initTabs(); initTabScroll(); initCarousels(); initColnav(); initFacets(); initCounters(); initPanels(); });

/* Collection sort: auto-submit on change (filters keep their explicit Apply button). */
document.addEventListener('change', (e) => {
  const s = e.target.closest('[data-auto-submit]');
  if (s) { const f = s.closest('form'); if (f) f.submit(); }
});

/* Enable scroll-snap only AFTER LCP is recorded (see theme.css NO_LCP note). */
addEventListener('load', () => setTimeout(() => document.documentElement.classList.add('snap-on'), 700));

/* ---- Double-submit guard: on a real (validated) form submission, disable the submit button and show a
   spinner so a slow response can't be double-submitted. AJAX /cart/add forms manage their own loading in
   cart.js (skipped here); JS-prevented / invalid submits are skipped via e.defaultPrevented and native
   validation. Buttons re-enable on navigation (pageshow) and via a safety timeout, so they never stick. ---- */
(function () {
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM' || e.defaultPrevented) return;
    if (/\/cart\/add/.test(form.getAttribute('action') || '')) return; // cart.js owns add-to-cart loading
    var btns = form.querySelectorAll('button[type="submit"], button:not([type]), input[type="submit"]');
    Array.prototype.forEach.call(btns, function (b) {
      if (b.disabled || b.hasAttribute('data-no-loading')) return;
      b.classList.add('is-loading');
      b.setAttribute('aria-busy', 'true');
      setTimeout(function () { b.disabled = true; }, 0); // after the submit starts, so name/value still posts
      var clear = function () { b.disabled = false; b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); };
      window.addEventListener('pageshow', clear, { once: true });
      setTimeout(clear, 15000); // safety net — never stay stuck
    });
  });
})();

/* ---------- Phone fields: accept only phone characters (digits + - ( ) space), site-wide ---------- */
$$('[data-phone-input]').forEach((el) => el.addEventListener('input', () => {
  const cleaned = el.value.replace(/[^0-9+()\-\s]/g, '');
  if (cleaned !== el.value) el.value = cleaned;
}));
